import React, { useEffect, useState } from 'react';

import { isEqual } from 'lodash';

import { warehouseCapacityListURL, warehouseLocationsURL, warehousePalletRequestsURL } from '@admin/config/routes';
import { usePermissionKey } from '@admin/hooks/use_permission';
import { client } from '@admin/libraries/apollo';
import { CustomerAccessHoursInput, useModifyWarehouseMutation, useWarehouseQuery, WarehouseInput } from '@admin/schema';
import { Button } from '@admin/styled/button';
import { ButtonLink, ExternalButtonLink } from '@admin/styled/button_link';
import { Card } from '@admin/styled/card';
import { CardToolbar } from '@admin/styled/card_toolbar';
import { ErrorBanner } from '@admin/styled/error_banner';
import { FormFooter } from '@admin/styled/form_footer';
import { PageSections } from '@admin/styled/page_sections';
import { Section } from '@admin/styled/section';
import { SectionActions } from '@admin/styled/section_actions';
import { SectionTitle } from '@admin/styled/section_title';

import { FeatureFlagsList } from '../feature_flags/list';
import { OperatingSchedule } from '../operating_schedule';
import { OrderLeadTimePanel } from '../order_lead_time/panel';
import { WarehouseOperationsDetail, WarehouseOperationsForm } from './warehouse_operations_form';

function sanitizeTime(string: string): string {
  if (['1:00', '2:00', '3:00', '4:00', '5:00', '6:00', '7:00', '8:00', '9:00'].includes(string)) {
    return `0${string}`;
  }

  return string;
}

function buildWarehouseInputFromQuery(
  warehouse: NonNullable<ReturnType<typeof useWarehouseQuery>['data']>['warehouse'],
): WarehouseInput {
  return {
    name: warehouse.name,
    abbreviation: warehouse.abbreviation,
    tz: warehouse.tz,
    active: warehouse.active,
    height: warehouse.height || undefined,
    thirdPartyLogistics: warehouse.thirdPartyLogistics,
    inboundDockItemsPerHour: warehouse.inboundDockItemsPerHour,
    outboundDockItemsPerHour: warehouse.outboundDockItemsPerHour,
    address: {
      aptsuite: warehouse.address.aptsuite || '',
      street: warehouse.address.street,
      city: warehouse.address.city,
      state: warehouse.address.state,
      country: warehouse.address.country || 'United States',
      zip: warehouse.address.zip,
      latitude: warehouse.address.latitude ?? undefined,
      longitude: warehouse.address.longitude ?? undefined,
    },
    regionIDs: warehouse.regions.map((region) => region.id),
    storageTreasuresFacilityID: warehouse.storageTreasuresFacilityID,
    customerAccessInstructions: warehouse.customerAccessInstructions,
    customerAccessHours:
      warehouse.customerAccessHours?.map(
        (accessHours) =>
          ({
            day: accessHours.day,
            open: sanitizeTime(accessHours.open ?? ''),
            close: sanitizeTime(accessHours.close ?? ''),
          }) as CustomerAccessHoursInput,
      ) ?? [],
  };
}

export const WarehouseSitePanels: React.FC<{
  warehouseId: string;
}> = ({ warehouseId }) => {
  const [savedWarehouse, setSavedWarehouse] = useState<WarehouseInput | undefined>(undefined);
  const [warehouse, setWarehouse] = useState<WarehouseInput | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [editingOtherDetails, setEditingOtherDetails] = useState(false);

  const { data, loading: loadingQuery } = useWarehouseQuery({ client, variables: { id: warehouseId } });
  const [execute, { loading: loadingMutation }] = useModifyWarehouseMutation({ client });

  const editAccess = usePermissionKey('facility:update');

  const onSave = async () => {
    if (loadingMutation || !warehouse) {
      return;
    }
    const response = await execute({ variables: { input: warehouse, id: warehouseId } });
    if (response?.data) {
      setError(response.data.modifyWarehouse.error ?? undefined);
      if (response.data.modifyWarehouse.error === null) {
        setEditingOtherDetails(false);
        setSavedWarehouse(warehouse);
      }
    }
  };

  const cancelEditing = () => {
    setWarehouse(savedWarehouse);
    setEditingOtherDetails(false);
    setError(undefined);
  };

  useEffect(() => {
    if (data?.warehouse && warehouse === undefined) {
      const newWarehouse = buildWarehouseInputFromQuery(data.warehouse);
      setSavedWarehouse(newWarehouse);
      setWarehouse(newWarehouse);
    }
  }, [data, warehouse]);

  if (!data?.warehouse) {
    return null;
  }

  return (
    <PageSections>
      <Section>
        <SectionTitle>Operating schedule</SectionTitle>
        <OperatingSchedule
          operatingScheduleTemplates={data.warehouse.operatingScheduleTemplates}
          manageHref={warehouseCapacityListURL()}
        />
      </Section>

      <Section>
        <SectionTitle>Order lead times</SectionTitle>
        <OrderLeadTimePanel warehouseID={data.warehouse.id} editAccess={editAccess} />
      </Section>

      <Section>
        <SectionTitle>Consumer Storage feature flags</SectionTitle>
        <FeatureFlagsList
          resourceName="Warehouse"
          resourceID={data.warehouse.id}
          editAccess={editAccess}
          emptyStateMessage="No feature flags are configured for Consumer Storage yet."
        />
      </Section>

      <Section>
        <SectionTitle>Consumer Storage settings</SectionTitle>
        <Card>
          <CardToolbar>
            <SectionActions style={{ width: '100%', justifyContent: 'flex-end' }}>
              {editAccess && !editingOtherDetails && (
                <Button variant="secondary" size="sm" type="button" onClick={() => setEditingOtherDetails(true)}>
                  Edit settings
                </Button>
              )}
              <ButtonLink variant="secondary" size="sm" to={warehouseLocationsURL(data.warehouse.id)}>
                View locations
              </ButtonLink>
              <ButtonLink variant="secondary" size="sm" to={warehousePalletRequestsURL(data.warehouse.id)}>
                View pallet requests
              </ButtonLink>
              {data.warehouse.storageTreasuresLink && (
                <ExternalButtonLink
                  variant="secondary"
                  size="sm"
                  href={data.warehouse.storageTreasuresLink}
                  target="_blank"
                  rel="noreferrer"
                >
                  Storage Treasures
                </ExternalButtonLink>
              )}
            </SectionActions>
          </CardToolbar>

          {error && (
            <div style={{ padding: '0 16px 16px' }}>
              <ErrorBanner role="alert">{error}</ErrorBanner>
            </div>
          )}

          {loadingQuery || !warehouse || !savedWarehouse ? null : editingOtherDetails ? (
            <form
              onSubmit={(event) => {
                event.preventDefault();
              }}
            >
              <WarehouseOperationsForm warehouse={warehouse} editAccess={true} onChange={setWarehouse} />
              <FormFooter>
                <Button variant="secondary" size="sm" type="button" onClick={cancelEditing} disabled={loadingMutation}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  type="button"
                  disabled={loadingMutation || isEqual(savedWarehouse, warehouse)}
                  onClick={() => void onSave()}
                >
                  {loadingMutation ? 'Saving…' : 'Save'}
                </Button>
              </FormFooter>
            </form>
          ) : (
            <WarehouseOperationsDetail warehouse={savedWarehouse} />
          )}
        </Card>
      </Section>
    </PageSections>
  );
};
